import { motion } from 'framer-motion'
import { Star, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { testimonials } from '@/data/mock-data'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

export default function TestimonialsSection() {
    const [currentIndex, setCurrentIndex] = useState(0)

    const nextTestimonial = () => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length)
    }

    const prevTestimonial = () => {
        setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
    }

    const currentTestimonial = testimonials[currentIndex]

    return (
        <section className="py-24 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-20">
                <div className="absolute top-1/2 left-0 w-96 h-96 bg-accent-gold/20 rounded-full blur-3xl" />
                <div className="absolute top-1/2 right-0 w-96 h-96 bg-primary-blue/20 rounded-full blur-3xl" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                {/* Section header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-accent-gold border border-accent-gold/30 mb-4">
                        Success Stories
                    </span>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                        What Our Students
                        <span className="block gradient-text">Are Saying</span>
                    </h2>
                    <p className="text-lg text-theme-secondary max-w-2xl mx-auto">
                        Join thousands of successful NEETaspirants who achieved their dreams with Future Founder
                    </p>
                </motion.div>

                {/* Testimonial carousel */}
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.5 }}
                    >
                        <GlassmorphicCard variant="elevated" className="p-8 md:p-12">
                            <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
                                {/* Avatar */}
                                <div className="flex-shrink-0">
                                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-blue to-accent-gold p-1">
                                        <div className="w-full h-full rounded-full bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                                            <span className="text-3xl font-bold gradient-text">
                                                {currentTestimonial.name.charAt(0)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 text-center md:text-left">
                                    {/* Rating */}
                                    <div className="flex gap-1 mb-4 justify-center md:justify-start">
                                        {[...Array(currentTestimonial.rating)].map((_, i) => (
                                            <Star key={i} className="h-5 w-5 fill-accent-gold text-accent-gold" />
                                        ))}
                                    </div>

                                    {/* Quote */}
                                    <p className="text-lg md:text-xl text-theme-primary mb-6 leading-relaxed italic">
                                        "{currentTestimonial.content}"
                                    </p>

                                    {/* Author */}
                                    <div>
                                        <p className="font-semibold text-theme-primary text-lg">
                                            {currentTestimonial.name}
                                        </p>
                                        <p className="text-sm text-theme-muted">
                                            {currentTestimonial.role}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* Navigation */}
                    <div className="flex items-center justify-center gap-4 mt-8">
                        <button
                            onClick={prevTestimonial}
                            className="p-3 rounded-full bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-theme-primary transition-all duration-200 hover:scale-110"
                            aria-label="Previous testimonial"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>

                        {/* Indicators */}
                        <div className="flex gap-2">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentIndex
                                        ? 'bg-primary-blue w-8'
                                        : 'bg-slate-600 hover:bg-slate-500'
                                        }`}
                                    aria-label={`Go to testimonial ${index + 1}`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={nextTestimonial}
                            className="p-3 rounded-full bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-theme-primary transition-all duration-200 hover:scale-110"
                            aria-label="Next testimonial"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>
        </section >
    )
}
